// AdjustGrant.jsx — wide, no scroll, single textarea (free text with inline quantities)
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import numberToWords from "number-to-words";
import "./GrantForm.css";
import GoldValuationModal from "./GoldValuationModal";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

/* ===== Helpers ===== */
const pad2 = (n) => String(n).padStart(2, "0");

const formatToDdMmYyyy = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(+dt)) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

// "12/03/2025" -> "2025-03-12T00:00:00Z"  (kept for compatibility if you still send ISO)
const toISOFromDdMmYyyy = (s) => {
  if (!s) return "";
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  const day = pad2(m[1]);
  const month = pad2(m[2]);
  const year = m[3];
  return `${year}-${month}-${day}T00:00:00`;
};

// util: split by comma, trim, drop empties (kept for legacy downstream if needed)
const splitItems = (raw) =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const sanitizeNumber = (s = "") => (s || "").replace(/,/g, "").trim();

/** Count items from free text */
const countItems = (text = "") => {
  const src = (text || "").toUpperCase();
  let total = 0;

  const nTok = /\b(\d+)\s*N\b/g;
  let m;
  while ((m = nTok.exec(src))) total += parseInt(m[1], 10);

  const bare = /\b(\d+)\s+(?!G(?:M|MS)?\b|GRAMS?\b|KG\b|OZ\b|CT\b|CARAT\S*\b|K\b|KT\b)\w+/g;
  const skipPurity = new Set([999, 916, 905, 900, 875, 750, 585, 417, 375]);
  while ((m = bare.exec(src))) {
    const n = parseInt(m[1], 10);
    if (skipPurity.has(n)) continue;
    total += n;
  }

  if (total === 0 && src.trim().length > 0) {
    total = src.split(",").map((s) => s.trim()).filter(Boolean).length;
  }
  return total;
};

export default function AdjustGrant() {
  useEscapeToGoBack();
  const navigate = useNavigate();
  const location = useLocation();

  // originals from previous page
  const passed = location.state || {};
  const originalReceiptNo = passed.receiptNo || "";
  const originalOldReceiptNo = passed.oldReceiptNo ?? passed.old_receipt_no ?? "";

  // Prefill item textarea
  const initialItemsInput = Array.isArray(passed.items)
    ? passed.items.map((i) => (typeof i === "string" ? i : i?.value || "")).join(", ")
    : passed.item || "";

  // Prefill dates if you’re displaying them (not the focus here, but kept intact)
  const initialGrant = passed.grantDate ? formatToDdMmYyyy(passed.grantDate) : "";
  const initialExpiry = passed.expiryDate ? formatToDdMmYyyy(passed.expiryDate) : "";

  // === NEW: editable receipt numbers ===
  const [receiptNoEdit, setReceiptNoEdit] = useState(originalReceiptNo);
  const [oldReceiptNoEdit, setOldReceiptNoEdit] = useState(originalOldReceiptNo);

  // for valuation modal prefill
  const [initialWeights, setInitialWeights] = useState(null);
  const [goldDetails, setGoldDetails] = useState(null);

  // form state
  const [itemsInput, setItemsInput] = useState((initialItemsInput || "").toUpperCase());
  const [remark, setRemark] = useState(passed.remark || "");
  const [principal, setPrincipal] = useState(
    passed.principal != null
      ? String(passed.principal)
      : passed.amount != null
      ? String(passed.amount)
      : ""
  );
  const [weight, setWeight] = useState(passed.weight != null ? String(passed.weight) : "");
  const [valuation, setValuation] = useState(
    passed.valuation != null
      ? String(passed.valuation)
      : passed.valuationAmount != null
      ? String(passed.valuationAmount)
      : ""
  );
  const [grantDate, setGrantDate] = useState(initialGrant);
  const [expiryDate, setExpiryDate] = useState(initialExpiry);
  const [staffCode, setStaffCode] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Tab-expand lookups
  const [itemMap, setItemMap] = useState({});
  const [materialMap, setMaterialMap] = useState({});
  const [conditionMap, setConditionMap] = useState({});
  const itemsRef = useRef(null);

  useEffect(() => {
    // Prevent stale gold details from a previous ticket being reused accidentally.
    try { localStorage.removeItem("pawnGoldDetails"); } catch {}

    axios
      .get("https://www.lianfoong.com/api/items/getAllItems")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((it) => {
          if (it?.code) map[it.code.toLowerCase()] = it.itemDesc || it.code;
        });
        setItemMap(map);
      })
      .catch(() => {});

    axios
      .get("https://www.lianfoong.com/api/materials/getAllMaterials")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((m) => {
          if (m?.code) map[m.code.toLowerCase()] = m.material || m.code;
        });
        setMaterialMap(map);
      })
      .catch(() => {});

    axios
      .get("https://www.lianfoong.com/api/item-conditions/getAllItemConditions")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((c) => {
          if (c?.code) map[c.code.toLowerCase()] = c.itemCondition || c.code;
        });
        setConditionMap(map);
      })
      .catch(() => {});

    axios
      .get("https://www.lianfoong.com/api/staff/session", { withCredentials: true })
      .then((res) => {
        if (res.data?.staffCode) setStaffCode(res.data.staffCode);
      })
      .catch(() => {});

    // If dates were not provided, default to today and +6 months
    if (!initialGrant || !initialExpiry) {
      const today = new Date();
      const expiry = new Date();
      expiry.setMonth(expiry.getMonth() + 6);
      expiry.setDate(expiry.getDate() - 1);
      setGrantDate(formatToDdMmYyyy(today));
      setExpiryDate(formatToDdMmYyyy(expiry));
    }

    // Prefill gold weights (kept intact)
    if (originalReceiptNo) {
      axios
        .get("https://www.lianfoong.com/api/pawn-gold/pledge", { params: { pledgeNo: originalReceiptNo } })
        .then(async (res) => {
          const pg = res.data || {};
          const prevWeights = {
            "999": String(pg.gold1Qty ?? ""),
            "24K": String(pg.gold2Qty ?? ""),
            "23K": String(pg.gold3Qty ?? ""),
            "22K": String(pg.gold4Qty ?? ""),
            "20K": String(pg.gold5Qty ?? ""),
            "18K": String(pg.gold6Qty ?? ""),
            "14K": String(pg.gold7Qty ?? ""),
            "9K": String(pg.gold8Qty ?? ""),
            "STONE": String(pg.gold9Qty ?? ""),
          };
          setInitialWeights(prevWeights);

          const { data: priceRows } = await axios.get("https://www.lianfoong.com/api/gold-prices");
          const prices = priceRows?.[0] || {};
          const keys = ["999", "24K", "23K", "22K", "20K", "18K", "14K", "9K", "STONE"];

          const sumVal =
            keys.reduce((acc, k) => acc + Number(prevWeights[k] || 0) * Number(prices[k] || 0), 0) +
            Number(pg.valAddAmt || 0);
          const sumW = keys.reduce((acc, k) => acc + Number(prevWeights[k] || 0), 0);

          setWeight(String(sumW.toFixed(1)));
          setValuation(String(sumVal.toFixed(2)));

          const loadedGoldDetails = {
            weights: prevWeights,
            prices,
            valAdd: String(pg.valAddAmt ?? "0"),
            totalWeight: sumW,
            totalValue: sumVal,
          };

          setGoldDetails(loadedGoldDetails);

          try {
            localStorage.setItem("pawnGoldDetails", JSON.stringify(loadedGoldDetails));
          } catch {}
        })
        .catch(() => {});
    }
  }, []); // once

  // Tab-to-expand in textarea
  const handleItemsKeyDown = (e) => {
    if (e.key !== "Tab" || e.shiftKey) return;
    const textarea = itemsRef.current;
    if (!textarea) return;

    const value = itemsInput || "";
    const cursor = textarea.selectionStart ?? value.length;

    const left = value.lastIndexOf(" ", Math.max(0, cursor - 1));
    const leftComma = value.lastIndexOf(",", Math.max(0, cursor - 1));
    const start = Math.max(left, leftComma) + 1;

    const rightSpace = value.indexOf(" ", cursor);
    const rightComma = value.indexOf(",", cursor);
    const rights = [rightSpace, rightComma].filter((x) => x !== -1);
    const end = rights.length ? Math.min(...rights) : value.length;

    const token = value.slice(start, end).trim();
    if (!token) return;

    const key = token.toLowerCase();
    const hit = itemMap[key] ?? materialMap[key] ?? conditionMap[key];
    if (!hit) return;

    e.preventDefault();
    const replacement = String(hit).toUpperCase();
    const newValue = value.slice(0, start) + replacement + value.slice(end);
    setItemsInput(newValue);

    requestAnimationFrame(() => {
      const pos = start + replacement.length;
      textarea.setSelectionRange(pos, pos);
      textarea.focus();
    });
  };

  const itemsCount = countItems(itemsInput);

  const convertToWords = (num) => {
    const clean = sanitizeNumber(num);
    return !clean || isNaN(Number(clean))
      ? ""
      : numberToWords.toWords(Number(clean)).toUpperCase() + " DOLLARS ONLY";
  };

  const handleSubmit = () => {
    const itemsArray = splitItems(itemsInput);

    const transactionData = {
      activeRecord: true,
      item: (itemsInput || "").trim(),
      weight: sanitizeNumber(weight),
      amount: sanitizeNumber(principal),
      valuationAmount: sanitizeNumber(valuation),
      remark,
      noOfItem: itemsCount,

      // === send edited receipt numbers ===
      receiptNo: (receiptNoEdit || "").trim(),
      oldReceiptNo: (oldReceiptNoEdit || "").trim(),

      // keep if you still use dates elsewhere
      grantDate: toISOFromDdMmYyyy(grantDate),
      expiryDate: toISOFromDdMmYyyy(expiryDate),

      handler: staffCode,

      // IMPORTANT: carry per-karat gold breakdown to AdjustPawner
      goldDetails,
    };

    navigate("/adjust-pawner", {
      state: {
        transactionData,
        // IMPORTANT: pass the ORIGINAL current receipt for backend lookup (@RequestParam)
        originalReceiptNo,
        items: itemsArray,
        staffCode,
      },
    });
  };

  return (
    <div className="grf-page grf-nosroll">
      <div className="grant-form grant-form--wide">
        {/* Header */}
        <div className="grf-header">
          <h2 className="grf-title">Grant Form (Adjust)</h2>
          <div className="grf-meta">
            <span className="grf-badge">Items: {itemsCount}</span>
          </div>
        </div>

        {/* Body — wide, no internal scrolling */}
        <div className="grf-body-noscroll">
          {/* Receipt numbers row */}
          <div className="grf-grid-12" style={{ marginBottom: 8 }}>
            <div className="grf-field col-6">
              <label className="grf-label">Current Receipt No</label>
              <input
                type="text"
                className="grf-input"
                value={receiptNoEdit}
                onChange={(e) => setReceiptNoEdit((e.target.value || "").toUpperCase())}
                placeholder="e.g., B/1025/0001"
              />
            </div>
            <div className="grf-field col-6">
              <label className="grf-label">Old Receipt No (optional)</label>
              <input
                type="text"
                className="grf-input"
                value={oldReceiptNoEdit}
                onChange={(e) => setOldReceiptNoEdit((e.target.value || "").toUpperCase())}
                placeholder="e.g., S/09258/0001 (or leave blank)"
              />
            </div>
          </div>

          <div className="grf-section-title"></div>
          <div className="grf-item-single">
            <label className="grf-label">Items</label>
            <textarea
              ref={itemsRef}
              className="grf-input grf-textarea"
              value={itemsInput}
              onChange={(e) => setItemsInput((e.target.value || "").toUpperCase())}
              onKeyDown={handleItemsKeyDown}
              rows={4}
            />
          </div>

          {/* Fields laid across 12 columns */}
          <div className="grf-grid-12">
            <div className="grf-field col-6">
              <label className="grf-label">Remark</label>
              <input
                type="text"
                className="grf-input"
                value={remark}
                onChange={(e) => setRemark((e.target.value || "").toUpperCase())}
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Amount</label>
              <div className="grf-stack">
                <input
                  type="text"
                  className="grf-input"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value.replace(/[^\d.]/g, ""))}
                />
                {principal && <span className="grf-amount-label">{convertToWords(principal)}</span>}
              </div>
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Weight</label>
              <input
                type="text"
                className="grf-input"
                value={weight}
                onChange={(e) => setWeight(e.target.value.replace(/[^\d.]/g, ""))}
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">No. of Items</label>
              <input type="text" className="grf-input" value={itemsCount} readOnly />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Valuation</label>
              <div className="grf-inline">
                <input type="text" className="grf-input" value={valuation} readOnly />
                <button type="button" className="grf-open" onClick={() => setIsModalOpen(true)}>
                  Open
                </button>
              </div>
            </div>

            {/* Dates (kept as-is) */}
            <div className="grf-field col-3">
              <label className="grf-label">Grant Date (dd/mm/yyyy)</label>
              <input
                type="text"
                className="grf-input"
                placeholder="dd/mm/yyyy"
                value={grantDate}
                onChange={(e) => setGrantDate(e.target.value)}
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Expiry Date (dd/mm/yyyy)</label>
              <input
                type="text"
                className="grf-input"
                placeholder="dd/mm/yyyy"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="grf-footer-nosticky">
          <button
            type="button"
            className="grf-btn grf-btn--ghost"
            onClick={() => navigate("/dashboard")}
          >
            Back
          </button>
          <button type="button" className="grf-btn grf-btn--primary" onClick={handleSubmit}>
            Next
          </button>
        </div>

        <GoldValuationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpdate={(newWeight, newValuation, details) => {
            const w = sanitizeNumber(newWeight);
            setWeight(w);
            const v = Number(sanitizeNumber(newValuation));
            setValuation(Number.isFinite(v) ? v.toFixed(2) : "");

            setGoldDetails(details);

            try { localStorage.setItem("pawnGoldDetails", JSON.stringify(details)); } catch {}
          }}
          initialWeights={initialWeights}
        />
      </div>
    </div>
  );
}
